/**
 * Thrivia Embed SDK: the card surface, and the DOM primitives every other surface reuses.
 *
 * Purpose: turn one `EmbedEventCard` from the public API into the widget an organizer pastes onto
 * their own website, and do it without ever handing a creator-supplied string to an HTML parser.
 *
 * What the card renders (spec section 6, product behaviour D8):
 *
 * | state       | badge     | body                                        | action                        |
 * |-------------|-----------|---------------------------------------------|-------------------------------|
 * | `live`      | none      | title, date, location, price, low-spots chip | "Book now" -> `card.url`      |
 * | `sold_out`  | Sold out  | title, date, location, price                | disabled, plus a capture slot |
 * | `ended`     | Ended     | title, date, location, "This event has ended" | "See other events", plus slot |
 * | `cancelled` | Cancelled | ... plus "This event has been cancelled"     | "View details" -> `card.url`  |
 * | `unknown`   | none      | title, date, location, price, and no claim  | "View details" -> `card.url`  |
 *
 * `unknown` is anything the API sent that this bundle does not recognise, and it makes NO statement
 * about the event in the card or in the JSON-LD. See {@link cardState}.
 *
 * The cover and the title are links to `card.url` in EVERY state. That is a correctness requirement
 * rather than a nicety: the organizer's baked permalink anchor is hidden while a surface renders and
 * the sold out action is deliberately disabled, so they are the ONLY route through to the event page
 * on the one state where a visitor most wants it. `ended` shows no price (R35).
 *
 * Four rules hold this file together, and each one is a defect this project has already paid for
 * somewhere:
 *
 * 1. **`card.url` verbatim.** Every href and every JSON-LD url is the string the API sent. The `@`
 *    and the `www` are load-bearing (Plan 1 shipped a Critical hand-building this URL), so nothing
 *    here concatenates `handle` and `slug`, and the creator link on an ended card is DERIVED from
 *    `card.url` by dropping its last segment rather than rebuilt from `handle`. A hand-built link is
 *    right on the happy path and wrong the moment the API's host, its casing or its handle
 *    normalization differ from what this bundle assumed years earlier.
 * 2. **No `innerHTML`, ever.** Titles, locations and cover URLs are creator-supplied. Text goes in
 *    through `textContent` and attributes are set individually, so `<img onerror=...>` in a title is
 *    six words of text and never a node. The JSON-LD payload additionally escapes `<`, because
 *    `JSON.stringify` does not and a `</script>` inside a title would otherwise be a parser hazard
 *    the day anything re-serializes the page.
 * 3. **The organizer's baked permalink anchor is untouchable.** It is the entire failure posture: on
 *    `error` and `missing` this renderer removes its own nodes and leaves the element holding
 *    exactly what the organizer wrote. `clearOwn` removes only children carrying `thrivia-r`.
 * 4. **The box is reserved before the data lands.** `mount()` runs at discovery, before any request
 *    resolves, and paints a skeleton built from the SAME classes as the real card, so the reserved
 *    height and the final height are the same number by construction. Layout shift on somebody
 *    else's homepage is a cost we impose on them and they cannot fix.
 *
 * Plan 3's notify-me capture: `sold_out` and `ended` cards carry the `thrivia-card__capture-slot`
 * element between the body and the action, exactly where Plan 2 put it, and it stays EMPTY until a
 * visitor asks for the form. What renders immediately is a one line trigger inside the BODY, in the
 * 26px the body already reserves for the live card's low-spots chip, so a card that turns out to be
 * sold out is the same height as the box painted for it. See `render/capture.ts`.
 *
 * No runtime dependencies, DOM only, ES2019 output target.
 */

import type { EmbedEventCard } from "../api";
import { DT } from "../origin";
import type { MountRecord, MountRenderer, MountTheme } from "../scan";
import { injectStyles } from "../styles";
import { mountCapture, type CaptureContext } from "./captureSlot";

/**
 * Class prefix for the card's own parts. Every class in the bundle starts with `thrivia-`.
 *
 * Exported so the button surface and the overlay's degraded panel build their class names from the
 * same constant instead of repeating the literal, which is both one place to rename and about ten
 * bytes a use on a page that ships under a hard cap.
 */
export const C = "thrivia-c__";

/** Marker + reset scope, on every element this bundle roots. `clearOwn` finds our nodes by it. */
export const OWN = "thrivia-r";

/** Added to the ORGANIZER's mount element while a surface is rendered. Hides their baked anchor. */
const MOUNTED = "thrivia-mounted";

/** Schema.org base, shared with the list surface so the string is bundled once. */
export const SCHEMA = "https://schema.org";

/** Availability turns "low" here. Spec section 6; matches the storefront's own urgency threshold. */
const LOW_SPOTS = 5;

/**
 * Only `http(s)` reaches an `href`.
 *
 * `card.url` is built by our own backend from a constant base, so this can never fire today. It is
 * here because an anchor href is the one genuine script sink on this surface, and a guard that
 * exists before it is needed is a guard that survives the API changing shape.
 */
export const HTTP_RE = /^https?:\/\//i;

/**
 * Visitor-locale FORMAT, event-local CLOCK. The two halves are separate decisions and only one of
 * them belongs to the visitor.
 *
 * `undefined` as the locale is the whole point of the first half: a Frenchman gets `12 oct.` and an
 * American gets `Oct 12`, because that is a presentation of the same fact and theirs is the one they
 * read fastest.
 *
 * `timeZone` IS PINNED, and that is the second half. It was omitted, which meant the instant was
 * re-expressed in whatever zone the visitor's machine is set to: an 08:00 IST departure read as
 * `19:30` on the PREVIOUS DAY from Los Angeles. That is not a localization, it is a different
 * answer to the question the card exists to answer. An event happens at a place, at the time the
 * organizer wrote on it, and "when does it start" means the clock on the ground — nobody boards a
 * kayak at their own device's local time.
 *
 * Asia/Kolkata specifically, because the platform's event times are IST-authored: the card carries
 * no timezone field to defer to, and this is the same call `formatPrice` makes one function below
 * when it pins `en-IN` because the number IS rupees. (It is NOT a house style copied from the app:
 * only `LeadFields.ts` and `AccommodationCard.tsx` pin it and `DomainTraffic.tsx` does not. It is
 * pinned here because a card is read worldwide and a rupee-priced Indian event's start time is a
 * fact about India.) The day it becomes wrong is the day the API sends a per-event timezone, which
 * is the field this should read instead.
 *
 * `timeZoneName` is what keeps that honest. Without it an LA visitor reads `12 Oct 2026, 08:00` and
 * has no way to know it is not theirs; with it, the card says whose clock it is.
 */
const DATE_OPTS: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZone: "Asia/Kolkata",
  timeZoneName: "short",
};

/** The badge copy per state. `live` has none, which is why this is a lookup and not a chain. */
const BADGE: Record<string, string> = {
  sold_out: "Sold out",
  ended: "Ended",
  cancelled: "Cancelled",
};

/** What this bundle knows how to say something about. `BADGE` covers three of the four. */
const KNOWN: Record<string, number> = { live: 1, sold_out: 1, ended: 1, cancelled: 1 };

/**
 * The card's state, or `unknown` for anything this bundle cannot make a true statement about.
 *
 * NOTHING VALIDATED THIS. `api.ts` checks only that a card has an id, and its `state` union is a
 * TypeScript type, which is a compile-time promise about a string that arrives at runtime from
 * another repository across a network. A renamed state, a new one, a null, a missing field: every
 * one of them fell through the render chain's final `else` and the card said "This event has been
 * cancelled", with a `thrivia-c--undefined` class, while `eventLd` on the same card told the
 * crawler `EventScheduled`. Opposite statements to the human and to the machine, on one page, in
 * bundles already cached on third-party sites where nothing can be recalled.
 *
 * `unknown` is the fail-safe because the safe thing to say is NOTHING. We do not claim the event is
 * cancelled, we do not claim it is bookable, and we do not claim it is sold out; the card shows what
 * we do know (title, date, location, price) with a link to the event page, which is authoritative
 * and one click away. The JSON-LD drops `eventStatus` and `availability` for the same reason: an
 * omitted optional field asserts nothing, where a guessed one asserts something false.
 *
 * Exported so `eventLd` and `buildCard` normalize through ONE function. Two normalizers is how the
 * human and the crawler come to disagree again.
 */
export function cardState(card: EmbedEventCard): string {
  const raw = card.state;
  return typeof raw === "string" && KNOWN[raw] ? raw : "unknown";
}

/**
 * Creates an element, optionally classed, texted and appended, in one call.
 *
 * Every DOM node in this bundle is built through here, which is what makes "no innerHTML" a property
 * of the code rather than a habit: there is no other way to put a string into the page.
 */
export function e(
  tag: string,
  cls: string,
  parent: Element | null,
  text?: string | null,
): HTMLElement {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text != null) node.textContent = text;
  if (parent) parent.appendChild(node);
  return node;
}

/**
 * Removes everything THIS bundle rendered into a mount, and nothing else.
 *
 * Backwards, because removing shortens the live `children` collection. Scoped to direct children
 * carrying `thrivia-r`, because the organizer's own markup lives in the same element and a mount
 * that has failed must be left holding exactly what they wrote: seven tests assert their permalink
 * anchor survives byte for byte, and one of them counts the element's descendants.
 */
export function clearOwn(el: HTMLElement): void {
  const kids = el.children;
  for (let i = kids.length - 1; i >= 0; i--) {
    const kid = kids[i];
    if (kid.classList && kid.classList.contains(OWN)) el.removeChild(kid);
  }
}

/**
 * Swaps whatever we had rendered for `node`, or for nothing at all.
 *
 * The node is built DETACHED and only then swapped in, so a throw part way through building leaves
 * the previous frame (a skeleton, or the organizer's own markup) standing rather than a half card.
 *
 * `MOUNTED` goes on while we are showing something and comes off the moment we are not, which is
 * what makes the baked permalink anchor reappear when the API fails after a skeleton was painted.
 */
export function swap(el: HTMLElement, node: HTMLElement | null): void {
  clearOwn(el);
  if (node) {
    el.appendChild(node);
    el.classList.add(MOUNTED);
  } else {
    el.classList.remove(MOUNTED);
  }
}

/**
 * ISO 8601 (or null) to the visitor's own locale.
 *
 * `start_date` is nullable on purpose: a series whose dates have all been cancelled can legitimately
 * have none, and a card that invents one is worse than a card without a date line.
 */
export function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  try {
    const at = new Date(iso);
    if (isNaN(at.getTime())) return null;
    return new Intl.DateTimeFormat(undefined, DATE_OPTS).format(at);
  } catch {
    return null;
  }
}

/**
 * Price line, or null when there is nothing to say.
 *
 * `en-IN` is pinned rather than left to the visitor, because the number IS rupees: rendering
 * `4,500` as `4.500` for a German visitor beside a `₹` is not a localization, it is a wrong price.
 * `min_price` is the minimum ticket price of the series, so the copy is "From". Zero is free rather
 * than "From ₹0", matching the storefront (`PricingSheet.tsx`).
 */
export function formatPrice(price: number | null): string | null {
  if (price == null) return null;
  if (price <= 0) return "Free";
  try {
    return "From ₹" + new Intl.NumberFormat("en-IN").format(Math.round(price));
  } catch {
    return null;
  }
}

/**
 * The creator's other events, derived from the canonical URL rather than rebuilt from `handle`.
 *
 * `https://www.thrivia.io/@handle/slug` -> `https://www.thrivia.io/@handle`. The two are the same
 * string today; they stop being the same string the moment the API serves a different host (staging,
 * a vanity domain) or normalizes a handle differently from how it stores it, and only one of them is
 * still correct then.
 */
export function creatorUrl(url: string): string {
  // The query, the fragment and any trailing slashes go first, or the "last segment" is `?utm=x`,
  // or the empty string after a trailing slash, and the cut lands in the wrong place. Organizer
  // pages append tracking parameters to everything and this string reaches an href.
  const clean = url.split("?")[0].split("#")[0].replace(/\/+$/, "");
  const cut = clean.lastIndexOf("/");
  // `cut < 9` clears `https://`, so a URL with no path at all never loses its host. A last segment
  // that is ALREADY a handle means this url is the creator page, and cutting again would hand back
  // the bare origin.
  if (cut < 9 || clean.charAt(cut + 1) === "@") return clean;
  return clean.slice(0, cut);
}

/** A `<script type="application/ld+json">` carrying `data`. Never executed; read by crawlers. */
export function ldScript(data: unknown): HTMLScriptElement {
  const node = document.createElement("script");
  node.type = "application/ld+json";
  // `JSON.stringify` does not escape `<`, so a title containing `</script>` would be a hazard for
  // anything that re-serializes and re-parses this page. textContent keeps the live DOM safe; the
  // escape keeps the serialized form safe too, and is what Next.js does for the same reason.
  node.textContent = JSON.stringify(data).replace(/</g, "\\u003c");
  return node;
}

/**
 * The `Event` entity for one card.
 *
 * Built as a plain object and handed to `JSON.stringify`, never concatenated: string-built JSON-LD
 * is how a quotation mark in a title becomes invalid structured data that no crawler reports on.
 *
 * `offers.url` is `card.url` exactly. Availability is `InStock` only while the event is actually
 * bookable, so sold out, ended and cancelled all map to `SoldOut`; `eventStatus` distinguishes
 * cancelled from everything else, which is the field Google reads for the strikethrough treatment.
 */
export function eventLd(card: EmbedEventCard): Record<string, unknown> {
  // The same allowlist the hrefs use. Data is not a sink, but a url we refuse to render is a url we
  // should not publish either: two guards that disagree are one guard nobody can reason about.
  const safe = HTTP_RE.test(card.url) ? card.url : "";
  const state = cardState(card);

  const data: Record<string, unknown> = {
    "@context": SCHEMA,
    "@type": "Event",
    name: card.title,
  };
  // Omitted entirely for `unknown`, which is what keeps this entity consistent with the card beside
  // it: neither of them claims a status we were not told.
  if (state !== "unknown") {
    data.eventStatus = SCHEMA + (state === "cancelled" ? "/EventCancelled" : "/EventScheduled");
  }
  if (safe) data.url = safe;

  // An Offer is emitted ONLY with a price. Google requires `price` AND `priceCurrency` whenever
  // `offers` is present, so a priceless card that published a bare availability would earn every
  // organizer a Search Console error on their own domain for a field they cannot fix.
  if (card.min_price != null) {
    const offers: Record<string, unknown> = {
      "@type": "Offer",
      price: card.min_price,
      priceCurrency: "INR",
    };
    // Same rule as `eventStatus`. `availability` is optional on an Offer (only `price` and
    // `priceCurrency` are required whenever `offers` is present), so an unknown state can simply
    // decline to answer rather than guess `SoldOut` at a live event or `InStock` at a dead one.
    if (state !== "unknown") {
      offers.availability = SCHEMA + (state === "live" ? "/InStock" : "/SoldOut");
    }
    if (safe) offers.url = safe;
    data.offers = offers;
  }
  // Emitted only when real. An entity with `"startDate": null` is worse than one without the key.
  if (card.start_date) data.startDate = card.start_date;
  if (card.location) data.location = { "@type": "Place", name: card.location };
  // Scheme-guarded for the reason stated at the top of this function: the cover the card refuses to
  // render is a cover this entity must not publish either, or the two guards disagree.
  if (card.cover_photo && HTTP_RE.test(card.cover_photo)) data.image = card.cover_photo;
  return data;
}

/**
 * Turns an anchor into a link. `target="_blank"` with `rel="noopener"` keeps the host's context.
 *
 * Exported because it is the single place `target` and `rel` are decided: the button surface and
 * the overlay's degraded link both go through it rather than repeating the pair, which is both one
 * decision to change and about sixty bytes each time it is not repeated.
 */
export function linkTo(node: HTMLElement, href: string): void {
  const link = node as HTMLAnchorElement;
  link.href = href;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
}

/**
 * Paints an organizer's colours onto a control we rendered.
 *
 * Inline styles rather than generated CSS rules, and that is the cheap answer as well as the right
 * one: a rule per organizer means a stylesheet that grows with configuration and a specificity
 * fight we have already nearly lost once (see the `:not(.thrivia-r)` note in `styles.ts`), while an
 * inline style wins outright and costs no bundle bytes at all. The values arrive pre-validated from
 * `readTheme` in `scan.ts`; nothing unvalidated reaches `style` here.
 */
export function themeCta(
  node: HTMLElement,
  bg: string | null,
  fg: string | null,
  sharp: boolean,
): void {
  const style = node.style;
  if (bg) style.background = bg;
  // A background with no foreground is the common case: the secondary button never takes one, and
  // the primary takes it only if the organizer set both. The class default underneath is dark text
  // on white, so `data-thrivia-color="#0B0B0B"` alone would paint black on black - a button that
  // is there, is clickable, and cannot be read. Derived rather than refused: the organizer asked
  // for that background and we can honour it legibly without a second attribute.
  if (bg || fg) style.color = fg || (light(bg) ? "#0B0B0B" : "#FFF");
  if (sharp) style.borderRadius = "4px";
}

/**
 * Is this background light enough to carry dark text?
 *
 * Perceived brightness, not the mean: green reads far brighter than blue at the same value, and a
 * mean puts white text on `#00FF00`. Only `#rgb` and `#rrggbb` are measured; the 4- and 8-digit
 * forms carry alpha over an unknown backdrop, so they take the dark default rather than a guess.
 */
function light(hex: string | null): boolean {
  if (!hex) return true;
  let body = hex.slice(1);
  if (body.length === 3) body = body[0] + body[0] + body[1] + body[1] + body[2] + body[2];
  if (body.length !== 6) return false;
  const n = parseInt(body, 16);
  /* eslint-disable no-bitwise */
  return ((n >> 16) * 299 + ((n >> 8) & 255) * 587 + (n & 255) * 114) / 1000 > 140;
  /* eslint-enable no-bitwise */
}

/**
 * The primary action, or nothing at all.
 *
 * A url that fails the scheme guard renders NO element rather than a hrefless one: a coral pill with
 * `cursor:pointer` that does nothing is a worse artifact than an absent button, and the visitor still
 * has the title and the cover as links.
 *
 * `id` is what makes the overlay CTA choice work, and it is stamped on the ANCHOR rather than read
 * off the mount. The mount-level click handler asks the clicked element which event it belongs to,
 * which is the only form of the question a list mount can answer at all: one mount, many cards, a
 * different event under every CTA. Passing no id means "this control is an ordinary link" - which
 * is exactly right for `See other events` and `View details`, neither of which should ever open a
 * checkout, and is how those two stay plain navigation without a second guard anywhere.
 */
function action(
  root: HTMLElement,
  label: string,
  href: string,
  id?: string | null,
  theme?: MountTheme | null,
): void {
  if (!HTTP_RE.test(href)) return;
  const node = e("a", C + "a", root, label);
  linkTo(node, href);
  if (id) node.setAttribute(DT + "id", id);
  if (theme) themeCta(node, theme.bg, theme.fg, theme.sharp);
}

/**
 * The notify-me slot, and its contents when we have what the endpoint needs.
 *
 * Plan 2 shipped this element empty and named. Plan 3 fills it: `capture.ts` owns the form, the
 * consent copy and the POST, and owns them in a file of their own because a visitor's contact
 * detail is the one string in this bundle that must never reach the organizer's page. The slot
 * element itself is unchanged, so an organizer who styled or hid it still gets what they asked for.
 *
 * `cap` is absent only when a caller has no mount context to give (the JSON-LD-only paths and any
 * future consumer of `buildCard`), and the slot then stays exactly as empty as it was.
 */
function captureSlot(
  root: HTMLElement,
  body: HTMLElement,
  card: EmbedEventCard,
  cap?: CaptureContext | null,
): void {
  const slot = e("div", "thrivia-card__capture-slot", root);
  // `body` as well as the slot: the trigger goes in the space the body already reserves, so the
  // card does not grow when its data lands. `capture.ts` carries the arithmetic.
  if (cap) mountCapture(slot, body, card.id, cap);
}

/**
 * One card, detached.
 *
 * Also used by the list surface, once per item, which is why it takes a card rather than a record
 * and returns a node rather than writing into the DOM.
 *
 * `cap` carries the two things the notify-me capture needs from the MOUNT rather than from the card
 * (the attribution sid its data arrived under, and whether this mount is a preview). It is optional
 * so that a caller with no mount in hand still gets a card, without a capture.
 */
export function buildCard(
  card: EmbedEventCard,
  cap?: CaptureContext | null,
  theme?: MountTheme | null,
): HTMLElement {
  const state = cardState(card);
  const root = e("div", OWN + " thrivia-c thrivia-c--" + state, null);

  // The same allowlist the JSON-LD uses. Empty means every link on this card is suppressed.
  const safe = HTTP_RE.test(card.url) ? card.url : "";

  // The cover and the title are links in EVERY state, which is not decoration. A sold out card's
  // action is deliberately disabled, so without these it would be the one state with no way through
  // to the event page at all, and it is the state where a visitor most wants it: waitlist, other
  // dates, the creator's other trips. The organizer's own permalink anchor is hidden while we render,
  // so this is the only route. An `<a>` with no href is a valid placeholder link, not focusable and
  // not clickable, which is what an unsafe url degrades to.
  const media = e("a", C + "m", root);
  if (safe) linkTo(media, safe);
  // The SAME http(s) allowlist the hrefs and the JSON-LD use. `img.src` is the only other URL sink
  // on this surface, and it is a real one: `javascript:` does not fire from an image, but `data:`
  // and `blob:` do render, so an API that ever returned one would paint arbitrary attacker-chosen
  // pixels inside an anchor on the organizer's page. A cover that fails renders NO `<img>` rather
  // than a broken one, which is the state the card already handles for a photoless event.
  if (card.cover_photo && HTTP_RE.test(card.cover_photo)) {
    const img = e("img", C + "i", media) as HTMLImageElement;
    // Individually set attributes, never a markup string.
    img.src = card.cover_photo;
    // The title, because the image is now the entire content of a link and a link with no accessible
    // name is a WCAG 2.4.4 failure. It also gives the broken-cover and images-off cases something to
    // show. The duplication with the title link below is redundant, which is allowed; nameless is not.
    img.alt = card.title;
    // setAttribute rather than the IDL property: `loading` is reflected only by engines that
    // implement it, and an engine that does not must still receive the attribute and ignore it.
    img.setAttribute("loading", "lazy");
  }
  const badge = BADGE[state];
  if (badge) e("span", C + "b" + (state === "sold_out" ? " " + C + "b--s" : ""), media, badge);

  const body = e("div", C + "y", root);
  const title = e("a", C + "t", body, card.title);
  if (safe) linkTo(title, safe);
  // The date survives cancellation on purpose: the people who most need a cancelled card are the
  // ones already holding tickets for that day.
  const when = formatDate(card.start_date);
  if (when) e("p", C + "d", body, when);
  if (card.location) e("p", C + "d", body, card.location);
  // Suppressed on `ended` (R35). "From ₹4,500" directly above a "See other events" button reads as
  // the price of THOSE events, and there is nothing on an ended card the number can be acted on. It
  // stays on `cancelled`, where it is a refund reference for the people holding tickets.
  const price =
    state === "ended" || (theme && theme.hidePrice) ? null : formatPrice(card.min_price);
  if (price) e("p", C + "p", body, price);

  if (state === "live") {
    // Urgency is claimed ONLY while the event is bookable. "Only 2 spots left" under a SOLD OUT
    // badge is the kind of contradiction that costs an organizer their visitor's trust, not ours.
    const left = card.spots_left;
    if (left != null && left > 0 && left <= LOW_SPOTS) {
      e("span", C + "s", body, "Only " + left + (left === 1 ? " spot left" : " spots left"));
    }
    // The secondary action, above the primary one so the eye reaches Book now last. Both halves
    // are required: a label with no destination is a button that does nothing, and a destination
    // with no label is a button with no name, which is a WCAG failure rather than a small gap.
    if (theme && theme.actionLabel && theme.actionHref) {
      const second = e("a", C + "a " + C + "a--2", root, theme.actionLabel);
      linkTo(second, theme.actionHref);
      themeCta(second, theme.actionBg, null, theme.sharp);
    }
    action(root, (theme && theme.label) || "Book now", card.url, card.id, theme);
  } else if (state === "sold_out") {
    captureSlot(root, body, card, cap);
    // A span, not an anchor: there is nothing to click, and a disabled-looking link that still
    // navigates is worse than no link. `aria-disabled` says the same thing to a screen reader.
    e("span", C + "a " + C + "a--o", root, "Sold out").setAttribute("aria-disabled", "true");
  } else if (state === "ended") {
    e("p", C + "d " + C + "n", body, "This event has ended");
    captureSlot(root, body, card, cap);
    action(root, "See other events", creatorUrl(card.url));
  } else {
    // `cancelled` says so; `unknown` says nothing, and the shared "View details" is what makes the
    // silence usable rather than a dead end. See `cardState`.
    if (state === "cancelled") e("p", C + "d " + C + "n", body, "This event has been cancelled");
    action(root, "View details", card.url);
  }

  return root;
}

/**
 * The height-reserving placeholder, detached.
 *
 * Every element reuses the real card's classes, so this is not "a skeleton shaped like a card", it
 * is the card's own box with grey blocks in it. `.thrivia-c__bar` is declared after the classes it
 * pairs with, so its background wins on source order while the box metrics come from the pair.
 */
export function buildSkeleton(): HTMLElement {
  const bar = " " + C + "bar";
  const root = e("div", OWN + " thrivia-c thrivia-c--sk", null);
  e("div", C + "m", root);
  const body = e("div", C + "y", root);
  e("div", C + "t" + bar, body);
  e("div", C + "d" + bar, body);
  e("div", C + "d" + bar, body);
  e("div", C + "p" + bar, body);
  e("div", C + "a" + bar, root);
  return root;
}

/**
 * One frame of a card mount.
 *
 * `pending` and `loading` reserve the box; `ready` draws the card and its JSON-LD; `error` and
 * `missing` render nothing and hand the element back exactly as the organizer wrote it.
 *
 * The JSON-LD lives INSIDE the card root, so replacing the root replaces the entity. Appending it
 * anywhere else is how a mount that renders three times (pending, loading, ready) ends up publishing
 * three copies of the same Event to a crawler.
 */
function paint(record: MountRecord): void {
  const state = record.state;
  let node: HTMLElement | null = null;

  if (state === "ready" && record.card) {
    injectStyles();
    // The sid belongs to the RESPONSE this mount's data came in, not to the page, which is why it
    // is read off the record here rather than held anywhere module level. `mode` rides along for
    // the same reason and is the fix for CO-6: the sid carries the surface of whichever event mount
    // came FIRST in the organizer's HTML, so a notify-me typed into this card was reaching the CRM
    // labelled with somebody else's surface.
    node = buildCard(
      record.card,
      { sid: record.sid, surface: record.mode, preview: record.preview },
      record.theme,
    );
    node.appendChild(ldScript(eventLd(record.card)));
  } else if (state === "pending" || state === "loading") {
    injectStyles();
    node = buildSkeleton();
  }

  swap(record.el, node);
}

/**
 * The card renderer, registered for mode `card` in `index.ts`.
 *
 * No `teardown`: nothing here attaches outside `record.el`, no observer, no window listener, no
 * timer, so a pruned mount takes everything we made with it when the host removes the element.
 */
export const cardRenderer: MountRenderer = { mount: paint, update: paint };
